import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import {
  Building2,
  Calendar,
  Clock,
  ArrowLeft,
  Users,
  CheckCircle,
  Heart,
  Briefcase,
  Award,
  Scale,
  AlertTriangle,
  Target
} from 'lucide-react';

export default function ClausesSocialesRSEArticle() {
  return (
    <>
      <SEO
        title="Clauses Sociales RSE Marchés Publics 2026 : Insertion, Handicap, Égalité"
        description="Guide complet des clauses sociales dans les marchés publics 2026 : insertion professionnelle, emploi des travailleurs handicapés, égalité femmes-hommes. Comment répondre concrètement aux exigences RSE."
        path="/blog/clauses-sociales-rse-marches-publics"
      />
      <div className="min-h-screen bg-white">
        <header className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <span className="font-bold text-xl">Mon DCE Express</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button></Link>
              <Link to="/register"><Button className="bg-white text-emerald-900 hover:bg-slate-100">S'inscrire</Button></Link>
            </div>
          </div>
        </header>

        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">RSE</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 text-white">NOUVEAU</span>
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="article-title">
                Clauses Sociales RSE dans les Marchés Publics 2026 : Insertion, Handicap, Égalité
              </h1>
              <div className="flex items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />Mai 2026</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4" />11 min de lecture</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-12 mb-12 text-white text-center">
              <Users className="w-20 h-20 mx-auto mb-4 opacity-90" />
              <p className="text-xl font-medium">Le pilier social de la RSE</p>
              <p className="text-emerald-100">30 % des marchés publics intègrent des clauses sociales en 2026</p>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                La <strong>Loi Climat et Résilience</strong> et le <strong>Code de la commande publique 2026</strong> imposent
                désormais aux acheteurs publics d'intégrer des considérations sociales dans <strong>tous les marchés</strong> de
                plus de 40 000 € HT. Si l'environnement est souvent au premier plan, le pilier <strong>social</strong> de la
                RSE devient un critère d'attribution décisif pour les PME qui savent se positionner.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg mb-8">
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-emerald-900 mb-2">Chiffres clés 2026</h4>
                    <ul className="text-emerald-800 space-y-1 text-sm">
                      <li>• <strong>30 %</strong> des marchés publics nationaux comportent une clause sociale</li>
                      <li>• <strong>10 à 20 %</strong> de pondération attribuée au critère social</li>
                      <li>• <strong>6 %</strong> de travailleurs handicapés exigés (Loi OETH)</li>
                      <li>• <strong>Index Égalité ≥ 75/100</strong> requis pour les entreprises &gt; 50 salariés</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Briefcase className="w-7 h-7 text-emerald-600" />
                1. La clause d'insertion professionnelle
              </h2>

              <p>
                La clause d'insertion oblige le titulaire d'un marché à réaliser un volume d'heures de travail
                au profit de personnes éloignées de l'emploi : demandeurs d'emploi de longue durée, bénéficiaires
                du RSA, jeunes sans qualification, travailleurs handicapés, etc.
              </p>

              <div className="bg-slate-100 rounded-xl p-6 my-8">
                <h4 className="font-bold text-slate-900 mb-4">Mécanique typique d'une clause d'insertion</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span><strong>5 à 10 %</strong> du nombre d'heures travaillées du marché dédiées à l'insertion</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span>Possibilité de <strong>sous-traiter</strong> à un acteur de l'IAE (Insertion par l'Activité Économique)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span>Suivi via le <strong>facilitateur clause sociale</strong> (gratuit, désigné par l'acheteur)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span>Pénalités possibles en cas de non-respect : <strong>jusqu'à 10 % du montant du marché</strong></span>
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Heart className="w-7 h-7 text-emerald-600" />
                2. Emploi des travailleurs handicapés (OETH)
              </h2>

              <p>
                Toute entreprise de <strong>20 salariés et plus</strong> doit employer au moins <strong>6 %</strong> de
                travailleurs handicapés (Loi OETH). Dans les marchés publics, l'acheteur peut désormais exiger une
                attestation AGEFIPH et pondérer ce critère.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-white border-2 border-emerald-200 rounded-xl p-6 shadow-sm">
                  <h4 className="font-bold text-slate-900 mb-3">✅ Comment se mettre en conformité</h4>
                  <ul className="text-slate-600 text-sm space-y-2">
                    <li>• Recruter directement des travailleurs RQTH</li>
                    <li>• Recourir à un ESAT ou EA (Entreprise Adaptée)</li>
                    <li>• Faire appel à un travailleur indépendant handicapé</li>
                    <li>• Verser une contribution AGEFIPH si déficit</li>
                  </ul>
                </div>
                <div className="bg-white border-2 border-emerald-200 rounded-xl p-6 shadow-sm">
                  <h4 className="font-bold text-slate-900 mb-3">📋 Pièces à fournir en candidature</h4>
                  <ul className="text-slate-600 text-sm space-y-2">
                    <li>• Attestation AGEFIPH année N-1</li>
                    <li>• Déclaration sur l'honneur (entreprises &lt; 20 salariés)</li>
                    <li>• Politique handicap (charte, accord d'entreprise)</li>
                    <li>• Référents handicap nommés</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Scale className="w-7 h-7 text-emerald-600" />
                3. Égalité Femmes-Hommes : l'Index obligatoire
              </h2>

              <p>
                Depuis 2019 et renforcé en 2026, l'<strong>Index Égalité Femmes-Hommes</strong> est exigé pour toute
                entreprise de plus de 50 salariés qui candidate à un marché public. Score minimum attendu :{' '}
                <strong>75/100</strong>.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-amber-900 mb-2">Attention exclusion</h4>
                    <p className="text-amber-800 text-sm">
                      Un Index inférieur à 75/100 pendant 3 années consécutives peut entraîner une{' '}
                      <strong>exclusion automatique</strong> des marchés publics. Calcul à publier sur le site web
                      d'entreprise + transmission à la DREETS.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Award className="w-7 h-7 text-emerald-600" />
                4. Comment se démarquer dans votre réponse
              </h2>

              <p>
                Au-delà de la simple conformité, voici les bonnes pratiques qui font la différence dans le mémoire
                technique :
              </p>

              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <span><strong>Quantifier</strong> vos engagements : nombre d'heures d'insertion, nombre de personnes RQTH, taux de formation interne, etc.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <span><strong>Joindre des preuves</strong> : labels (Diversité, Égalité, Lucie, B-Corp), conventions signées avec Pôle Emploi, accords d'entreprise</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <span><strong>Mettre en récit</strong> : présentez 1-2 cas concrets d'insertion réussie sur des marchés similaires</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <span><strong>Proposer un référent social</strong> dédié au marché, identifié nominativement</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <span><strong>Calendrier de mise en œuvre</strong> détaillé sur la durée du marché</span>
                </li>
              </ul>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 my-8">
                <h4 className="font-bold text-emerald-900 mb-3">Labels RSE qui rassurent les acheteurs</h4>
                <p className="text-emerald-800 text-sm">
                  <strong>Lucie 26000</strong> (référentiel ISO 26000), <strong>B-Corp</strong> (impact global),{' '}
                  <strong>EcoVadis</strong> (notation RSE 0-100), <strong>Label Diversité AFNOR</strong>,{' '}
                  <strong>Label Égalité Professionnelle AFNOR</strong>. Investir dans 1 ou 2 labels augmente
                  significativement vos chances de remporter des marchés axés RSE.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">Conclusion</h2>

              <p>
                Les clauses sociales ne sont plus une option : elles deviennent un véritable critère de
                <strong> sélection</strong> et parfois d'<strong>exclusion</strong>. Les PME qui prennent les
                devants en se structurant autour de l'insertion, du handicap et de l'égalité gagnent un avantage
                concurrentiel mesurable. À l'inverse, l'absence de réponse formalisée sur ces points peut coûter
                jusqu'à <strong>20 % du score final</strong>.
              </p>

              <p>
                Bonne nouvelle : la plupart des dispositifs (facilitateurs, IAE, AGEFIPH) sont{' '}
                <strong>gratuits ou subventionnés</strong>. Il suffit de bien les connaître et de les activer.
              </p>
            </div>

            <div className="mt-12 p-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Anticipez les marchés à forte exigence RSE</h3>
              <p className="text-emerald-100 mb-6">
                Notre IA détecte les marchés intégrant des clauses sociales et vous prépare un mémoire technique adapté.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-slate-100">Essai gratuit 15 jours</Button>
              </Link>
            </div>
          </div>
        </article>

        <footer className="bg-slate-900 text-slate-400 py-8">
          <div className="container mx-auto px-4 text-center">
            <p>© 2026 Mon DCE Express - Tous droits réservés</p>
          </div>
        </footer>
      </div>
    </>
  );
}
